import {
  configuredSections,
  DETAIL_ITEM_PREFIX,
  serviceRequestFieldIsMulti,
  serviceRequestFieldIsQuantityList,
  serviceRequestFieldLabel,
  serviceRequestFieldOptions,
  serviceRequestSchemaFieldType,
  type ServiceRequestType,
} from './service-requests';

/** One answer read from pasted notes, before anyone accepts it. */
export type NotesAnswer = {
  /** `detail:<field>` for built-in request fields, otherwise the question key. */
  item: string;
  label: string;
  value: string | number | boolean;
  /** The words in the notes it came from, so people can check it. */
  from: string;
};

type Kind = 'choice' | 'boolean' | 'integer' | 'date' | 'text';

type Question = { item: string; field: string; label: string; kind: Kind; options: string[] };

/** Built-in request fields the reader can fill, with the kind of value each takes. */
const DETAIL_KINDS: Record<string, Kind> = {
  customer: 'text',
  venue_site: 'text',
  opportunity_reference: 'text',
  business_reason: 'text',
  target_go_live_date: 'date',
  required_completion_date: 'date',
};

/** Other words people use for a question, matched against "Label: value" lines. */
const SYNONYMS: Record<string, string[]> = {
  customer: ['customer', 'client', 'company', 'company name', 'customer name', 'account'],
  venue_site: ['venue', 'site', 'venue / site', 'location'],
  opportunity_reference: ['deal', 'opportunity', 'pipedrive deal'],
  target_go_live_date: ['go live', 'go-live', 'go live date', 'go-live date', 'launch date'],
  main_customer_contact_name: ['contact', 'contact name', 'main contact', 'customer contact', 'name'],
  customer_email: ['email', 'e-mail', 'email address', 'contact email'],
  customer_phone: ['phone', 'telephone', 'tel', 'mobile', 'phone number', 'contact number'],
  number_of_machines: ['machines', 'number of machines', 'devices', 'number of devices', 'units', 'qty', 'quantity'],
  approximate_location_count: ['locations', 'number of locations', 'sites', 'number of sites'],
  country_region: ['country', 'region', 'country / region'],
  machine_model_type: ['machine', 'machine model', 'model', 'machine type', 'device type'],
  target_installation_date: ['install date', 'installation date', 'install', 'installation'],
  company_type: ['company type', 'sector', 'customer type'],
  deal_status: ['deal status', 'status'],
  deployment_type: ['deployment type', 'deployment'],
  known_locations: ['known locations', 'addresses', 'site list'],
};

/** Phrases in loose prose that mean one choice. Only used when exactly one choice matches. */
const CHOICE_HINTS: Record<string, Array<[RegExp, string]>> = {
  deal_status: [
    [/\bcontract (?:is |has been |was )?signed\b|\bsigned (?:the )?contract\b/i, 'Contract signed'],
    [/\bcontract (?:is |has been |was )?sent\b|\bsent (?:them |over )?(?:the )?contract\b/i, 'Contract sent'],
    [/\bverbal(?:ly)? (?:agreed|yes|agreement)\b/i, 'Verbally agreed'],
  ],
  company_type: [
    [/\buniversit(?:y|ies)\b|\bcampus\b/i, 'University'],
    [/\bairports?\b/i, 'Airport'],
    [/\bgyms?\b/i, 'Gym'],
    [/\bretail(?:er)?\b/i, 'Retail'],
  ],
  deployment_type: [
    [/\btrial\b/i, 'Trial'],
    [/\bsample\b/i, 'Sample'],
    [/\bevent\b/i, 'Event'],
    [/\bpaid (?:deployment|deal|contract)\b/i, 'Paid'],
  ],
  commercial_ownership_model: [
    [/\b(?:lease|rental|rent)\b/i, 'Lease or Rental'],
    [/\bservice agreement\b/i, 'Service Agreement'],
  ],
};

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

function normal(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9/& ]+/g, ' ').replace(/\s+/g, ' ').trim();
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/** Reads UK-style dates ("12/11/2026", "12 Nov", "12th November 2026", "Nov 12") and ISO dates as yyyy-mm-dd. */
export function readDate(text: string, today: Date = new Date()): string | null {
  const iso = text.match(/\b(\d{4})-(\d{2})-(\d{2})\b/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const numeric = text.match(/\b(\d{1,2})[/.](\d{1,2})[/.](\d{2,4})\b/);
  if (numeric) {
    const day = Number(numeric[1]);
    const month = Number(numeric[2]);
    const year = Number(numeric[3].length === 2 ? `20${numeric[3]}` : numeric[3]);
    if (day >= 1 && day <= 31 && month >= 1 && month <= 12) return `${year}-${pad(month)}-${pad(day)}`;
  }
  const month = '(jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)[a-z]*';
  const dayFirst = text.match(new RegExp(`\\b(\\d{1,2})(?:st|nd|rd|th)?\\s+(?:of\\s+)?${month}\\.?(?:,?\\s+(\\d{4}))?`, 'i'));
  const monthFirst = text.match(new RegExp(`\\b${month}\\.?\\s+(\\d{1,2})(?:st|nd|rd|th)?(?:,?\\s+(\\d{4}))?`, 'i'));
  const parts = dayFirst
    ? { day: Number(dayFirst[1]), month: dayFirst[2], year: dayFirst[3] }
    : monthFirst
      ? { day: Number(monthFirst[2]), month: monthFirst[1], year: monthFirst[3] }
      : null;
  if (!parts || parts.day < 1 || parts.day > 31) return null;
  const monthIndex = MONTHS.indexOf(parts.month.slice(0, 3).toLowerCase());
  if (monthIndex < 0) return null;
  let year = parts.year ? Number(parts.year) : today.getFullYear();
  if (!parts.year) {
    const candidate = new Date(year, monthIndex, parts.day);
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    if (candidate < startOfToday) year += 1;
  }
  return `${year}-${pad(monthIndex + 1)}-${pad(parts.day)}`;
}

function questionsOf(type: ServiceRequestType): Question[] {
  const items = configuredSections(type).flatMap((section) => section.items);
  return items.flatMap((item): Question[] => {
    if (item.startsWith(DETAIL_ITEM_PREFIX)) {
      const field = item.slice(DETAIL_ITEM_PREFIX.length);
      const kind = DETAIL_KINDS[field];
      return kind ? [{ item, field, label: serviceRequestFieldLabel(field, type), kind, options: [] }] : [];
    }
    if (serviceRequestFieldIsMulti(type, item) || serviceRequestFieldIsQuantityList(type, item)) return [];
    if (type.field_schema?.must_confirm?.includes(item)) return [];
    const options = serviceRequestFieldOptions(type, item);
    const fieldType = serviceRequestSchemaFieldType(type, item);
    const kind: Kind = options.length
      ? 'choice'
      : fieldType === 'boolean'
        ? 'boolean'
        : fieldType === 'positive_integer'
          ? 'integer'
          : fieldType === 'date'
            ? 'date'
            : 'text';
    return [{ item, field: item, label: serviceRequestFieldLabel(item, type), kind, options }];
  });
}

/** Turns what was written into the value the question takes, or null if it does not fit. */
function convert(question: Question, raw: string, today: Date): string | number | boolean | null {
  const text = raw.trim();
  if (!text) return null;
  if (question.kind === 'choice') {
    const wanted = normal(text);
    const exact = question.options.find((option) => normal(option) === wanted);
    if (exact) return exact;
    const inside = question.options.filter((option) => !['tbc', 'other'].includes(normal(option)) && wanted.includes(normal(option)));
    if (inside.length === 1) return inside[0];
    const hinted = (CHOICE_HINTS[question.field] ?? []).filter(([pattern, option]) => pattern.test(text) && question.options.includes(option));
    if (hinted.length === 1) return hinted[0][1];
    if (/^(?:tbc|to be confirmed)$/i.test(text) && question.options.includes('TBC')) return 'TBC';
    if (/^(?:yes|y)$/i.test(text) && question.options.includes('Yes')) return 'Yes';
    if (/^(?:no|n)$/i.test(text) && question.options.includes('No')) return 'No';
    return null;
  }
  if (question.kind === 'boolean') {
    if (/^(?:yes|y|true|done|completed|agreed|confirmed)\b/i.test(text)) return true;
    if (/^(?:no|n|false|not yet|pending)\b/i.test(text)) return false;
    return null;
  }
  if (question.kind === 'integer') {
    const match = text.match(/\b(\d{1,5})\b/);
    const n = match ? Number(match[1]) : NaN;
    return Number.isInteger(n) && n > 0 ? n : null;
  }
  if (question.kind === 'date') return readDate(text, today);
  return text;
}

function labelMatches(question: Question, label: string): boolean {
  const wanted = normal(label);
  if (!wanted) return false;
  if (normal(question.label) === wanted || normal(question.field.replaceAll('_', ' ')) === wanted) return true;
  if (wanted.includes(' ') && normal(question.label).startsWith(`${wanted} `)) return true;
  return (SYNONYMS[question.field] ?? []).some((synonym) => normal(synonym) === wanted);
}

/**
 * Reads answers out of a pasted email, call notes or meeting notes. Nothing leaves the browser.
 * First "Label: value" lines, then emails, phone numbers, counts, dates and choice phrases in the prose.
 * Questions already answered (in `answered`) are left alone.
 */
export function readNotes(
  text: string,
  type: ServiceRequestType,
  answered: ReadonlySet<string> = new Set(),
  today: Date = new Date(),
): NotesAnswer[] {
  const questions = questionsOf(type).filter((question) => !answered.has(question.item));
  const found = new Map<string, NotesAnswer>();
  const add = (question: Question | undefined, value: string | number | boolean | null, from: string) => {
    if (!question || value === null || found.has(question.item)) return;
    found.set(question.item, { item: question.item, label: question.label, value, from: from.trim().slice(0, 140) });
  };
  const byField = (field: string) => questions.find((question) => question.field === field);

  for (const line of text.split(/\r?\n/)) {
    const match = line.match(/^\s*[-*•]?\s*([A-Za-z][A-Za-z0-9 /&()'-]{1,60}?)\s*[:\u2013\u2014-]\s+(.+)$/);
    if (!match) continue;
    const question = questions.find((candidate) => labelMatches(candidate, match[1]));
    if (question) add(question, convert(question, match[2], today), line);
  }

  const email = text.match(/[\w.+-]+@[\w-]+(?:\.[\w-]+)+/);
  if (email) add(byField('customer_email'), email[0], email[0]);
  const phone = text.match(/(?:\+\d{1,3}[\s-]?)?(?:\(0\)\s?)?\d[\d\s-]{8,14}\d/);
  if (phone && phone[0].replace(/\D/g, '').length >= 10) add(byField('customer_phone'), phone[0].trim(), phone[0]);

  const machines = text.match(/\b(\d{1,4})\s+(?:x\s+)?(?:[a-z-]+\s+)?(?:machines?|devices?|units?|fridges?|kiosks?)\b/i);
  if (machines) add(byField('number_of_machines'), Number(machines[1]), machines[0]);
  const locations = text.match(/\b(\d{1,4})\s+(?:locations?|sites?|venues?|campuses)\b/i);
  if (locations) {
    const count = Number(locations[1]);
    add(byField('approximate_location_count'), count, locations[0]);
    const scope = byField('location_scope');
    if (scope) add(scope, convert(scope, count > 1 ? 'Multiple locations' : 'Single location', today), locations[0]);
  }

  const install = text.match(/\binstall(?:ation|ed|ing)?\b[^.,;\n]{0,40}/i);
  const installDate = install ? readDate(install[0], today) : null;
  if (install && installDate) {
    const question = byField('target_installation_date');
    add(question, question?.kind === 'date' ? installDate : install[0].replace(/^install(?:ation|ed|ing)?\s*(?:on|by|date|for|from)?\s*/i, ''), install[0]);
  }
  const live = text.match(/\bgo(?:ing)?[\s-]?live\b[^.,;\n]{0,40}/i);
  const liveDate = live ? readDate(live[0], today) : null;
  if (live && liveDate) add(byField('target_go_live_date'), liveDate, live[0]);

  for (const question of questions) {
    if (question.kind !== 'choice' || found.has(question.item)) continue;
    const hits = (CHOICE_HINTS[question.field] ?? []).filter(([pattern, option]) => pattern.test(text) && question.options.includes(option));
    const options = Array.from(new Set(hits.map(([, option]) => option)));
    if (options.length === 1) {
      const hit = hits[0][0].exec(text);
      add(question, options[0], hit ? hit[0] : options[0]);
    }
  }

  return questions.filter((question) => found.has(question.item)).map((question) => found.get(question.item) as NotesAnswer);
}

/** Splits accepted answers into built-in request fields and question answers. */
export function splitAnswers(answers: NotesAnswer[]): { details: Record<string, string>; typeData: Record<string, string | number | boolean> } {
  const details: Record<string, string> = {};
  const typeData: Record<string, string | number | boolean> = {};
  for (const answer of answers) {
    if (answer.item.startsWith(DETAIL_ITEM_PREFIX)) details[answer.item.slice(DETAIL_ITEM_PREFIX.length)] = String(answer.value);
    else typeData[answer.item] = answer.value;
  }
  return { details, typeData };
}
